export type GameAction =
  | 'moveLeft'
  | 'moveRight'
  | 'softDrop'
  | 'rotateClockwise'
  | 'rotateCounterClockwise'
  | 'hardDrop'
  | 'hold'
  | 'pause'
  | 'restart';

export type KeyBindings = Record<GameAction, string>;

export interface AppSettings {
  keyBindings: KeyBindings;
  showGhost: boolean;
  motionEffects: boolean;
  touchHaptics: boolean;
}

export const KEY_BINDING_DEFINITIONS: readonly { action: GameAction; label: string }[] = [
  { action: 'moveLeft', label: 'Move left' },
  { action: 'moveRight', label: 'Move right' },
  { action: 'softDrop', label: 'Soft drop' },
  { action: 'rotateClockwise', label: 'Rotate clockwise' },
  { action: 'rotateCounterClockwise', label: 'Rotate counter-clockwise' },
  { action: 'hardDrop', label: 'Hard drop' },
  { action: 'hold', label: 'Hold piece' },
  { action: 'pause', label: 'Pause / resume' },
  { action: 'restart', label: 'Restart' },
];

export const DEFAULT_KEY_BINDINGS: KeyBindings = {
  moveLeft: 'ArrowLeft',
  moveRight: 'ArrowRight',
  softDrop: 'ArrowDown',
  rotateClockwise: 'ArrowUp',
  rotateCounterClockwise: 'KeyZ',
  hardDrop: 'Space',
  hold: 'KeyC',
  pause: 'Escape',
  restart: 'Shift+KeyR',
};

export const DEFAULT_SETTINGS: AppSettings = {
  keyBindings: DEFAULT_KEY_BINDINGS,
  showGhost: true,
  motionEffects: true,
  touchHaptics: true,
};

const MODIFIER_CODES = new Set(['AltLeft', 'AltRight', 'ControlLeft', 'ControlRight', 'MetaLeft', 'MetaRight', 'ShiftLeft', 'ShiftRight']);

export function bindingFromKeyboardEvent(event: Pick<KeyboardEvent, 'altKey' | 'code' | 'ctrlKey' | 'metaKey' | 'shiftKey'>): string | null {
  if (!event.code || MODIFIER_CODES.has(event.code)) return null;
  const parts: string[] = [];
  if (event.ctrlKey) parts.push('Control');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  if (event.metaKey) parts.push('Meta');
  parts.push(event.code);
  return parts.join('+');
}

export function formatBinding(binding: string): string {
  const labels: Record<string, string> = {
    Alt: 'ALT',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    ArrowUp: '↑',
    Backspace: 'BACKSPACE',
    Control: 'CTRL',
    Delete: 'DELETE',
    Enter: 'ENTER',
    Escape: 'ESC',
    Meta: 'CMD',
    Shift: 'SHIFT',
    Space: 'SPACE',
    Tab: 'TAB',
  };
  return binding.split('+').map((part) => {
    if (labels[part]) return labels[part];
    if (part.startsWith('Key')) return part.slice(3);
    if (part.startsWith('Digit')) return part.slice(5);
    return part.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
  }).join(' + ');
}

export function rebindKey(bindings: KeyBindings, action: GameAction, binding: string): KeyBindings {
  const next = { ...bindings };
  const conflict = KEY_BINDING_DEFINITIONS.find(({ action: candidate }) => (
    candidate !== action && bindings[candidate] === binding
  ));
  if (conflict) next[conflict.action] = bindings[action];
  next[action] = binding;
  return next;
}

export function parseStoredSettings(value: unknown): AppSettings {
  if (!value || typeof value !== 'object') return { ...DEFAULT_SETTINGS, keyBindings: { ...DEFAULT_KEY_BINDINGS } };
  const candidate = value as Partial<AppSettings>;
  const storedBindings = candidate.keyBindings && typeof candidate.keyBindings === 'object' ? candidate.keyBindings : {};
  const keyBindings = { ...DEFAULT_KEY_BINDINGS };

  for (const { action } of KEY_BINDING_DEFINITIONS) {
    const binding = (storedBindings as Partial<KeyBindings>)[action];
    if (typeof binding === 'string' && binding.length > 0) keyBindings[action] = binding;
  }

  return {
    keyBindings,
    showGhost: typeof candidate.showGhost === 'boolean' ? candidate.showGhost : DEFAULT_SETTINGS.showGhost,
    motionEffects: typeof candidate.motionEffects === 'boolean' ? candidate.motionEffects : DEFAULT_SETTINGS.motionEffects,
    touchHaptics: typeof candidate.touchHaptics === 'boolean' ? candidate.touchHaptics : DEFAULT_SETTINGS.touchHaptics,
  };
}
